/**
 * 04 · Összesítés. Handover §5.10, §5.11, §9.1, §9.6, §11.8 and §5.7 for the
 * callback, with DR-6, DR-11, DR-13, DR-14, DR-15, DR-16 and DR-20 applied as
 * `build/FIXES.md` records them.
 *
 * §5.11: the event line, the food by category — a price per category,
 * quantities per dish and **no price per dish** — the service, and the
 * totals. A consumer sees gross; a business buyer sees net, then VAT and
 * gross.
 */

import type { OrderLine } from '@plt/engine'
import { budapestDate, eventTimeLabel } from '@plt/engine'
import { LEGAL_DOCS, type LegalDoc } from '@plt/emails'

import type { PageBundle } from '../bundle.ts'
import { Note } from '../components.tsx'
import { budapestWallClock, sentence } from '../format.ts'
import { MinimumOrderLine, PickupCallout, mealNote } from './Notices.tsx'
import { SimplePayLogo, SimplePayNotice, SimplePayTestLabel } from './SimplePay.tsx'
import { contactMissing } from './Step3Contact.tsx'

export function Step4Summary({ page }: { page: PageBundle }): JSX.Element {
  const { t, state } = page

  return (
    <section id="sec4">
      <div className="sechead">
        <span className="n">04</span>
        {/* The event name is written as text, never as markup: #sumTitle has
            been a textContent since vB4 and item 28 pinned it rather than
            changing it. React escapes it by construction. */}
        <h2 id="sumTitle">{state.eventName.trim() || t('Összesítés', 'Summary')}</h2>
      </div>

      {/* Fix list item 9 (Barna, 10 October 2026): the side panel is the one
          summary. The event-and-price card that stood here repeated it; run
          13a item 3 deleted it (it was dead code, last in git at 670801f). */}
      <Payment page={page} />
      <Terms page={page} />
      {/* `build/FIXES.md` item 79 (F8): "at the end of the order, before the
          order button", and **always available** -- it is not conditional on
          anything, because writing in it is one of the seven things that
          sends the order for confirmation. */}
      <Comment page={page} />
      <SubmitBlock page={page} />
      <Callback page={page} />
      <DonePanel page={page} />
    </section>
  )
}

/** A legal document's page, in a new tab: the order being placed stays. */
function LegalLink(props: { doc: LegalDoc; lang: 'hu' | 'en'; children: string }): JSX.Element {
  return (
    <a href={LEGAL_DOCS[props.doc][props.lang].path} target="_blank" rel="noopener" data-legal={props.doc}>
      {props.children}
    </a>
  )
}

/** The ordered portions of a category, added up per unit, in first-seen order. */
export function unitTotals(lines: readonly OrderLine[]): [string, number][] {
  const totals = new Map<string, number>()
  for (const line of lines) totals.set(line.unit, (totals.get(line.unit) ?? 0) + line.orderedPortions)
  return [...totals.entries()]
}

/**
 * §5.11: a dish line carries its quantity and its unit and **no price**. The
 * English unit has to agree with the count; the Hungarian numeral leaves its
 * noun singular, so *1 adag* is right as it stands and *pcs* is invariant.
 */
export const unitWord = (page: PageBundle, unit: string, count: number): string =>
  unit === 'adag'
    ? page.t('adag', `portion${page.f.plural(count, '', 's')}`)
    : page.t('db', 'pcs')

/**
 * §9.1: every order is paid in full before it is actioned.
 *
 * DR-14 (`build/FIXES.md` item 17), option B: §14's first-person plural for
 * the company binds the English too — the company says what it does rather
 * than disappearing into a passive. The buttons stay imperative.
 */
/**
 * `build/FIXES.md` item 79 (F8): the confirmation note, in the brief's own
 * words, with the times from the parameters.
 *
 * One component, used in every place the note belongs — above the order
 * button, and in place of the enquiry note F1 shows — so the four readings
 * cannot come apart. It **replaces the former enquiry note everywhere except
 * on the short-notice enquiry**, which keeps its own note and its own route.
 */
export function ConfirmationNote({ page }: { page: PageBundle }): JSX.Element {
  const { t } = page
  return (
    <Note>
      {t(
        `Ajánlatát munkatársunk átnézi, és szükség esetén módosítja. Most nem kell fizetnie: a visszaigazolás után e-mailben küldjük a fizetési linket. Igyekszünk 24 órán belül válaszolni.`,
        `A colleague will check your offer and adjust it if needed. You don't pay now: once we have confirmed it, we email you a payment link. We aim to answer within 24 hours.`,
      )}
    </Note>
  )
}

/**
 * `build/FIXES.md` item 79 (F8): *Megjegyzés vagy különleges kérés*, always
 * available, at the end of the order and before the order button.
 *
 * The label, the placeholder and the hint are the brief's own words and are
 * final. 500 characters, which is the field's own limit and the column's.
 */
function Comment({ page }: { page: PageBundle }): JSX.Element {
  const { t, state, set, locked } = page
  return (
    <div className="card" id="commentCard">
      <h3>{t('Megjegyzés vagy különleges kérés', 'Comments or special requests')}</h3>
      <textarea
        id="comment"
        rows={3}
        maxLength={500}
        disabled={locked}
        aria-describedby="commentHint"
        placeholder={t(
          'pl. a terem 2. emeleten van, lift nincs',
          'e.g. the room is on the 2nd floor, no lift',
        )}
        value={state.comment}
        onChange={(event) => set({ comment: event.target.value.slice(0, 500) })}
      />
      <p className="sub" id="commentHint" style={{ marginTop: 5 }}>
        {t(
          'Kérjük, ne írjon ide nevet vagy más személyes adatot, csak az igényt.',
          "Please don't include names or other personal details here, only the need.",
        )}
      </p>
    </div>
  )
}

/**
 * redesign_payment_photos, card payment decision 1 (DECIDED 20261101): while
 * `card_payment_route` is `dijbekero` (the seeded default, and what a missing
 * parameter means) the card is paid through the szamlazz.hu díjbekérő, so
 * the page offers one payment method instead of the card/transfer choice.
 */
export const dijbekeroRoute = (param: (key: string) => string): boolean =>
  (param('card_payment_route').trim() || 'dijbekero') !== 'simplepay'

/**
 * SimplePay test prompt of 3 November 2026, §1 (DECIDED 20261103, Barna): the
 * SimplePay route is `card_payment_route = simplepay`, or the díjbekérő route
 * for a buyer the server says is on `simplepay_test_emails`. The page learns
 * that as true or false (`state.simplepayTest`); the server decides the lane
 * again from the email on the order, so this only decides what is drawn.
 */
export const simplePayRoute = (page: Pick<PageBundle, 'param' | 'state'>): boolean =>
  !dijbekeroRoute(page.param) || page.state.simplepayTest

/** §5 and §6: the card is paid on SimplePay's page now, so the notice applies. */
export const simplePayCardNow = (page: PageBundle): boolean =>
  simplePayRoute(page) &&
  !page.confirm &&
  !page.enquiry &&
  (page.state.payment === 'card' || page.rules.short)

function SimplePayBlock({ page }: { page: PageBundle }): JSX.Element {
  const { t, state, set, locked } = page
  return (
    <div id="spBlock" data-simplepay="block">
      {state.simplepayTest ? <SimplePayTestLabel t={t} /> : null}
      <SimplePayLogo t={t} />
      {simplePayCardNow(page) ? (
        <SimplePayNotice
          t={t}
          checked={state.acceptSimplePay}
          disabled={locked}
          onChange={(checked) => set({ acceptSimplePay: checked })}
        />
      ) : null}
    </div>
  )
}

/**
 * Card payment change 2: the one payment method, with the deadline the
 * server computes — `payment_window_bd` business days from the díjbekérő,
 * capped at the order deadline (the short-notice one on that lane). Kept to
 * this block and its own copy so the redesign carries it over unchanged.
 */
function DijbekeroPayment({ page }: { page: PageBundle }): JSX.Element {
  const { t, f, calendar, param } = page
  const days = f.n(Number(param('payment_window_bd') || 0))
  const freeze = (() => {
    if (!calendar.ok) return '—'
    if (calendar.lane === 'short') {
      if (calendar.shortNoticeDeadline === null) return '—'
      const clock = budapestWallClock(calendar.shortNoticeDeadline)
      return `${f.date(clock.day, true)} ${clock.hhmm}`
    }
    return calendar.freezeAt === null ? '—' : `${f.date(calendar.freezeAt, true)} ${param('freeze_time')}`
  })()
  return (
    <div id="payRoute" data-pay="dijbekero">
      <p style={{ margin: '4px 0 0' }}>
        <b>
          {t(
            'Bankkártyával vagy átutalással, díjbekérő alapján.',
            'By card or bank transfer, against a payment request.',
          )}
        </b>
      </p>
      <p className="sub" style={{ margin: '4px 0 0' }}>
        <i>
          {t(
            `A díjbekérőt e-mailben küldjük, benne a bankkártyás fizetés linkjével és az utalási adatokkal. Fizetési határidő: ${days} munkanap, de legkésőbb ${freeze}.`,
            `We email you a payment request with a card payment link and our bank details. Pay within ${days} business days, and by ${freeze} at the latest.`,
          )}
        </i>
      </p>
    </div>
  )
}

export function Payment({ page }: { page: PageBundle }): JSX.Element {
  const { t, state, set, rules, enquiry, confirm, locked } = page
  return (
    <div className="card">
      <h3>{t('Fizetés', 'Payment')}</h3>
      <div id="payBox">
        {confirm ? (
          /* `build/FIXES.md` item 79 (F8): "When at least one confirmation
             reason applies, the payment choice (card or transfer) is not
             shown." The card stays, with the note in place of the choice, so
             the customer is told why there is nothing to choose rather than
             finding the section gone. */
          <ConfirmationNote page={page} />
        ) : enquiry ? (
          <p className="sub">
            {t(
              'Ajánlatkérésnél most nem kell fizetni. Kollégánk felhívja, és az egyeztetett rendelést fizetésre küldjük.',
              'Nothing is paid with an enquiry. A colleague calls you, and the agreed order is sent to you for payment.',
            )}
          </p>
        ) : !simplePayRoute(page) ? (
          /* Card payment change 2: the short-notice lane too, and its
             "reserve now, charge on confirmation" copy is not shown. */
          <DijbekeroPayment page={page} />
        ) : rules.short ? (
          <>
          <Note tone="ok">
            {t(
              /* `build/FIXES.md` item 83 (P4), ruling 1 of 8 October 2026 (2):
                 the sentence saying the amount is reserved now and charged on
                 confirmation, and the one saying a single invoice is issued,
                 are deleted, and nothing is put in their place. What is left
                 is vB6's first word, in both languages. */
              'Bankkártya.',
              'Card.',
            )}
          </Note>
          <SimplePayBlock page={page} />
          </>
        ) : (
          <>
            <div className="opts" style={{ marginTop: 8 }}>
              <button
                type="button"
                data-pay="card"
                aria-pressed={state.payment === 'card'}
                disabled={locked}
                onClick={() => set({ payment: 'card' })}
              >
                {t('Bankkártya', 'Card')}
              </button>
              <button
                type="button"
                data-pay="transfer"
                aria-pressed={state.payment === 'transfer'}
                disabled={locked}
                onClick={() => set({ payment: 'transfer' })}
              >
                {t('Átutalás díjbekérő alapján', 'Bank transfer against a payment request')}
              </button>
            </div>
            <SimplePayBlock page={page} />
            {/* Run 10 item 6: what each method means, and the date, is said
                once, in *Fizetés és lemondás* below. The transfer note that
                gave the payment window here is gone with the other copy that
                told the customer how long they had ("Remove any other copy
                telling the customer how long they have").

                Run 16 item 4 (DECIDED 20261015, Barna): **a private person may
                choose transfer on exactly the business buyers' terms.** The
                sentence that limited it to companies is gone, with nothing in
                its place, and the button is no longer disabled for a consumer.
                The card lanes are untouched; the choice is the customer's. */}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Run 10 item 6: the first line of *Fizetés és lemondás*, one of three. The
 * confirmation lane (any reason, capacity_over included) has no payment
 * choice and pays nothing yet; otherwise card, the default, is paid now on
 * SimplePay's page, and a transfer is final when the money arrives by the
 * payment deadline — run 11 item 5: díjbekérő + payment_window_bd business
 * days, capped at the order deadline, as the server computes it.
 */
function PaymentLine({ page, deadline }: { page: PageBundle; deadline: string }): JSX.Element | null {
  const { t, state, rules, enquiry, confirm } = page
  if (enquiry) return null
  // Card payment change 2: under the díjbekérő route the method and its
  // deadline are said once, in the payment block above.
  if (!confirm && !simplePayRoute(page)) return null
  const method = confirm ? 'confirm' : rules.short || state.payment === 'card' ? 'card' : 'transfer'
  return (
    <p id="payLine" data-payline={method}>
      <b>{t('Fizetés:', 'Payment:')}</b>{' '}
      {method === 'confirm'
        ? t(
            'a visszaigazolás után, bankkártyával vagy átutalással. Addig nem terhelünk semmit.',
            'after we confirm, by card or bank transfer. Nothing is charged until then.',
          )
        : method === 'card'
          ? t(
              'bankkártyával, most, a SimplePay biztonságos oldalán. A teljes összeget egyben terheljük, és a rendelés a sikeres fizetéssel válik véglegessé. A számlát e-mailben küldjük.',
              'by card, now, on SimplePay’s secure page. We charge the full amount in one go, and the order is final once the payment succeeds. We email you the invoice.',
            )
          : t(
              `átutalással. Díjbekérőt küldünk; a rendelés akkor válik véglegessé, ha az összeg ${deadline}-ig beérkezik.`,
              `by bank transfer. We send a payment request; the order is final once the amount arrives by ${deadline}.`,
            )}
    </p>
  )
}

/** §5.10 and §11.8: the deadlines and the terms for this order's dates. */
function Terms({ page }: { page: PageBundle }): JSX.Element {
  const { t, f, state, set, calendar, rules, enquiry, confirm, num, param, locked } = page
  /* Barna's ruling 8 of 5 October 2026 (`build/FIXES.md` item 44): a change
     or cancellation fee is quoted on the amount the buyer sees. A business
     buyer reads net prices throughout, so the ladder stays on *a nettó ár*;
     a consumer reads one figure, the order's végösszeg, and a ladder quoted
     on a number the page never showed them is the wrong quote. The charged
     amount is the same either way — only the base word differs.

     The Hungarian rungs need the genitive, which is where the two forms part:
     *a nettó ár 30%-a* against *a rendelés végösszegének 30%-a*. So the
     constant holds the form each rung needs rather than a nominative that
     would have to be inflected at every use. */
  const feeBase = state.business ? 'a nettó ár' : 'a rendelés végösszegének'
  const feeBaseEn = state.business ? 'of the net price' : 'of the order total'
  /* Run 16 item 3(d) (DECIDED 20261015, Barna): the **modification** fee is
     taken on the lines the change moves, not on the whole order, so its base
     phrase is its own. The cancellation keeps the wording above, and each
     buyer type still reads only its own base word. */
  const feeBaseMod = state.business
    ? 'a módosított tételek nettó értékének'
    : 'a módosított tételek értékének'
  const feeBaseModEn = state.business
    ? 'of the net value of the changed items'
    : 'of the value of the changed items'
  const freeze =
    calendar.ok && calendar.freezeAt !== null
      ? `${f.date(calendar.freezeAt, true)} ${param('freeze_time')}`
      : '—'
  // Run 11 item 5c: the transfer line names the payment deadline; the
  // free-change line keeps the order deadline.
  const payBy = (() => {
    if (!calendar.ok || calendar.transferPaymentDeadline === null) return '—'
    const clock = budapestWallClock(calendar.transferPaymentDeadline)
    return `${f.date(clock.day, true)} ${clock.hhmm}`
  })()
  return (
    <div className="card">
      {/* Run 10 item 6: *Határidők és feltételek* becomes *Fizetés és
          lemondás* — how this order is paid, then what a change or a
          cancellation costs, with the order deadline named once. */}
      <h3>{t('Fizetés és lemondás', 'Payment and cancellation')}</h3>
      <div id="termsBox">
        {!calendar.ok ? (
          <p className="sub">{t('Adja meg a dátumot.', 'Enter the date.')}</p>
        ) : calendar.lane === 'std' ? (
          <>
            <PaymentLine page={page} deadline={payBy} />
            <p id="cancelLine">
              {t.lang === 'hu' ? (
                <>
                  <b>Módosítás és lemondás:</b> {freeze}-ig díjmentes. Utána a módosítás díja{' '}
                  {feeBaseMod} {f.n(num('fee_mod_after_freeze_pct'))}%-a, a lemondásé {feeBase}{' '}
                  {f.n(num('fee_cancel_after_freeze_pct'))}%-a; az esemény előtti{' '}
                  {f.n(num('fee_window_h'))} órában a módosításé {feeBaseMod}{' '}
                  {f.n(num('fee_within_window_pct'))}%-a, a lemondásé {feeBase}{' '}
                  {f.n(num('fee_within_window_pct'))}%-a.
                </>
              ) : (
                <>
                  <b>Changes and cancellation:</b> free of charge until {freeze}. After that a change
                  costs {num('fee_mod_after_freeze_pct')}% {feeBaseModEn} and a cancellation{' '}
                  {num('fee_cancel_after_freeze_pct')}% {feeBaseEn}; within {num('fee_window_h')} hours
                  of the event, a change costs {num('fee_within_window_pct')}% {feeBaseModEn} and a
                  cancellation {num('fee_within_window_pct')}% {feeBaseEn}.
                </>
              )}
            </p>
          </>
        ) : calendar.lane === 'short' ? (
          <>
            <PaymentLine page={page} deadline={payBy} />
            <p>
              <b>
                {t(
                  'Ez a rendelés már a díjköteles időszakban jön létre: nincs díjmentes módosítás vagy lemondás.',
                  'This order is placed inside the fee period: there is no free change or cancellation.',
                )}
              </b>
            </p>
            <p className="sub">
              {calendar.insideFeeWindow
                ? t(
                    `Az eseményig kevesebb mint ${f.n(num('fee_window_h'))} óra van: a módosítás díja ${feeBaseMod} ${f.n(num('fee_within_window_pct'))}%-a, a lemondásé ${feeBase} ${f.n(num('fee_within_window_pct'))}%-a.`,
                    `The event is less than ${num('fee_window_h')} hours away: a change costs ${num('fee_within_window_pct')}% ${feeBaseModEn} and a cancellation ${num('fee_within_window_pct')}% ${feeBaseEn}.`,
                  )
                : t(
                    `Módosítás: ${feeBaseMod} ${f.n(num('fee_mod_after_freeze_pct'))}%-a, lemondás: ${feeBase} ${f.n(num('fee_cancel_after_freeze_pct'))}%-a; az esemény előtti ${f.n(num('fee_window_h'))} órában a módosításé ${feeBaseMod} ${f.n(num('fee_within_window_pct'))}%-a, a lemondásé ${feeBase} ${f.n(num('fee_within_window_pct'))}%-a.`,
                    `A change costs ${num('fee_mod_after_freeze_pct')}% ${feeBaseModEn} and a cancellation ${num('fee_cancel_after_freeze_pct')}% ${feeBaseEn}; within ${num('fee_window_h')} hours of the event, a change costs ${num('fee_within_window_pct')}% ${feeBaseModEn} and a cancellation ${num('fee_within_window_pct')}% ${feeBaseEn}.`,
                  )}
            </p>
          </>
        ) : (
          <Note tone="stop">
            {t(
              'Erre a dátumra már nem tudunk rendelést felvenni.',
              'We can no longer take an order for this date.',
            )}
          </Note>
        )}

        {/* `build/FIXES.md` item 79 (F8): when the order is going for
            confirmation the confirmation note is what says what happens next,
            and it is shown above the button. §11.4's own note is the
            pay-first lane's and would contradict it — it says *paid orders*,
            and this one is not paid. */}
        {!enquiry && !confirm && calendar.ok && calendar.lane === 'std' ? (
          rules.review.length > 0 ? (
            <Note>
              <b>
                {t('Ezt a rendelést kollégánk hagyja jóvá.', 'A colleague approves this order.')}
              </b>{' '}
              {t(
                'Igyekszünk 24 órán belül válaszolni. Ha nem tudjuk vállalni, a teljes összeget visszakapja.',
                'We aim to answer within 24 hours. If we cannot take it, you get the full amount back.',
              )}
            </Note>
          ) : (
            <Note tone="ok">
              {t(
                'A fizetés után automatikusan visszaigazoljuk.',
                'We confirm automatically once you have paid.',
              )}
            </Note>
          )
        ) : null}
      </div>

      <label className="chk" style={{ marginTop: 12 }}>
        <input
          type="checkbox"
          id="t1"
          checked={state.acceptTerms}
          disabled={locked}
          onChange={(event) => set({ acceptTerms: event.target.checked })}
        />
        <span id="t1txt">
          {/* Legal pages brief (13 October 2026), item 4: the ÁSZF is accepted
              and the privacy notice is read, not accepted. Both names open
              the static page in a new tab, so the order on screen stays. */}
          {t.lang === 'hu' ? (
            <>
              Elfogadom az <LegalLink doc="aszf" lang="hu">ÁSZF-et</LegalLink>, és elolvastam az{' '}
              <LegalLink doc="privacy" lang="hu">adatkezelési tájékoztatót</LegalLink>.
            </>
          ) : (
            <>
              I accept the <LegalLink doc="aszf" lang="en">Terms and Conditions</LegalLink> and have read the{' '}
              <LegalLink doc="privacy" lang="en">Privacy Notice</LegalLink>.
            </>
          )}
          {/* Fix list item 9: both acceptances are required. */}
          <span className="req">*</span>
        </span>
      </label>
      {/* `.chk` is `display:flex`, which beats the `hidden` attribute. */}
      <label className={`chk${enquiry ? ' hide' : ''}`} style={{ marginTop: 8 }}>
        <input
          type="checkbox"
          id="t2"
          checked={state.acceptCancellation}
          disabled={locked}
          onChange={(event) => set({ acceptCancellation: event.target.checked })}
        />
        <span id="t2txt">
          {t(
            'Külön elfogadom a lemondási és módosítási feltételeket, valamint az allergén- és étrendi tájékoztatót.',
            'I separately accept the cancellation and modification terms and the allergen and dietary notice.',
          )}
          <span className="req">*</span>
        </span>
      </label>
      <div className={`note${enquiry ? ' hide' : ''}`} id="ackNote">
        {t(
          'A rendelés beérkezéséről azonnal értesítést küldünk. Ez nem elfogadás: a szerződés a külön visszaigazoló levéllel jön létre.',
          'We acknowledge your order at once. That is not acceptance: the contract forms with the separate confirmation email.',
        )}
      </div>
    </div>
  )
}

/**
 * The button, and what stands in its way. DR-13 (`build/FIXES.md` item 16),
 * option C: the *Még szükséges* lead-in is gone and each blocker is its own
 * sentence, capitalised, with a full stop.
 */
function SubmitBlock({ page }: { page: PageBundle }): JSX.Element {
  const { t, state, rules, enquiry, confirm, stale, locked } = page
  const blockers: string[] = []
  if (enquiry) {
    if (contactMissing(state, t, true).length > 0) {
      blockers.push(t('Hiányzó adat van a 03. pontban.', 'Details are missing in section 03.'))
    }
    if (!state.acceptTerms) {
      blockers.push(
        // Legal pages brief, item 4: the privacy notice is read, not
        // accepted, so the blocker names the ÁSZF only.
        t('Fogadja el az ÁSZF-et.', 'Please accept the Terms and Conditions.'),
      )
    }
  } else {
    blockers.push(...rules.block)
    if (contactMissing(state, t, false).length > 0) {
      blockers.push(t('Hiányzó adat van a 03. pontban.', 'Details are missing in section 03.'))
    }
    if (!state.acceptTerms || !state.acceptCancellation) {
      blockers.push(t('Fogadja el a feltételeket.', 'Please accept the terms.'))
    }
    // SimplePay test prompt §6: no start call without the SP doc §8 notice.
    if (simplePayCardNow(page) && !state.acceptSimplePay) {
      blockers.push(
        t('Fogadja el az adattovábbítási nyilatkozatot.', 'Please accept the data transfer notice.'),
      )
    }
  }
  /* The page is being brought up to date against an answer the customer has
     just changed. Nothing on screen is wrong, but it is not yet this answer's,
     and an order is not placed against a figure that is about to move. */
  if (stale) {
    blockers.push(t('Frissítjük az adatokat. Egy pillanat.', 'We are updating the figures. One moment.'))
  }

  return (
    <>
      {/* `build/FIXES.md` item 79 (F8): the confirmation note, "shown above
          the button whenever a confirmation reason applies". */}
      {/* Barna, 10 October 2026: the note is already in the payment card
          just above, so it is not repeated over the button. */}
      <div id="confirmNote" />
      {/* Run 17 item B7: the minimum order value, on the summary as well as
          on the offer card — where the customer is about to order and the
          block would otherwise be their first notice of it. */}
      <MinimumOrderLine page={page} id="sumMinimum" />
      {/* Run 17 item B11(c), as run 19 §4 extends it: the meal answer, in
          either direction. */}
      {mealNote(page) === null ? null : (
        <p className="sub" id="sumMealNote" data-mealnote="light" style={{ margin: '6px 0 0' }}>
          {mealNote(page)}
        </p>
      )}
      {/* Run 19 §8: the collection is not on the event date. Last thing
          before the order button, where it cannot be missed. */}
      <PickupCallout page={page} id="sumPickupAlert" />
      <div id="blockNote">
        {blockers.length > 0 && !locked ? (
          <Note>
            {blockers.map((blocker, index) => (
              <div key={index}>{sentence(blocker)}</div>
            ))}
          </Note>
        ) : null}
      </div>
      <button
        className="cta"
        id="go"
        type="button"
        disabled={blockers.length > 0 || locked || state.submitting}
        onClick={page.submit}
      >
        {/* `build/FIXES.md` item 79 (F8): "the order button reads *Elküldöm
            visszaigazolásra* / *Send for confirmation*. Otherwise the page is
            unchanged, including the existing order button." */}
        {enquiry
          ? t('Ajánlatot kérek – visszahívjuk', 'Request an offer – we call you back')
          : confirm
            ? t('Elküldöm visszaigazolásra', 'Send for confirmation')
            : simplePayRoute(page) && (state.payment === 'card' || rules.short)
              ? /* Run 10 item 6: card is the default and is paid now. Under
                   the díjbekérő route nothing is paid on the page, so the
                   transfer lane's button is the one. */
                t('Megrendelem és fizetek bankkártyával', 'Order and pay by card')
              : t('Megrendelem fizetési kötelezettséggel', 'I order with an obligation to pay')}
      </button>
      {state.submitError === null ? null : (
        <Note tone="stop" id="submitError">
          {t.of(state.submitError)}
        </Note>
      )}
    </>
  )
}

/** §5.7: the callback route stays secondary — plain text, no button styling. */
function Callback({ page }: { page: PageBundle }): JSX.Element {
  const { t, state, set, enquiry, param, locked } = page
  return (
    <>
      <div className="after" id="cbLink">
        {!enquiry && !locked && state.callbackDone === null ? (
          <button
            type="button"
            className="quiet"
            id="cbOpen"
            onClick={() => set({ callbackOpen: !state.callbackOpen })}
          >
            {t(
              'Bizonytalan a mennyiségekben? Kérjen visszahívást.',
              'Unsure about the quantities? Ask us to call you.',
            )}
          </button>
        ) : null}
      </div>
      <div id="cbPanel">
        {state.callbackDone !== null ? (
          <div className="cb">
            <p style={{ marginTop: 0 }}>
              <b>{t('Visszahívást kért.', 'Callback requested.')}</b>{' '}
              {t(
                `Kollégánk felhívja a ${state.callbackDone.phone} számon.`,
                `A colleague will call you on ${state.callbackDone.phone}.`,
              )}
            </p>
          </div>
        ) : state.callbackOpen ? (
          <div className="cb">
            <p style={{ marginTop: 0 }}>
              {t(
                'Kollégánk felhívja, és átnézik együtt a mennyiségeket. Amit eddig összeállított, megkapjuk.',
                'A colleague calls you to go through the quantities together. We receive what you have put together so far.',
              )}
            </p>
            <label className="f" htmlFor="cbTel">
              {t('Telefonszám', 'Phone number')}
              <span className="req">*</span>
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="tel"
                id="cbTel"
                className={state.callbackBad ? 'missing' : undefined}
                style={{ maxWidth: 240 }}
                placeholder="+36 30 123 4567"
                /* DR-8 (build/FIXES.md item 11), option B: the callback field
                   mirrors the step 03 number until the customer edits it, so a
                   number entered in step 03 afterwards still reaches the panel. */
                value={state.callbackTouched ? state.callbackField : state.phone}
                onChange={(event) =>
                  set({ callbackField: event.target.value, callbackTouched: true, callbackBad: false })
                }
              />
              <button type="button" className="cta" id="cbSend" onClick={page.sendCallback}>
                {t('Visszahívást kérek', 'Ask for a call')}
              </button>
            </div>
            <p className="sub">
              {t('Hívhat minket is:', 'You can also call us:')}{' '}
              {param('callback_phone') === ''
                ? t('[a telefonszám még nincs kijelölve]', '[number not yet assigned]')
                : param('callback_phone')}
            </p>
          </div>
        ) : null}
      </div>
    </>
  )
}

/**
 * The confirmation, drawn from the record written at the moment the order was
 * sent and from nothing else (DR-6, `build/FIXES.md` item 9) — so switching
 * language translates it instead of leaving it in the language it was written
 * in, and nothing else on the page can rewrite it. Dates are kept as instants
 * and formatted here, because their format is a language matter.
 */
function DonePanel({ page }: { page: PageBundle }): JSX.Element | null {
  const { t, f, state } = page
  const sent = state.sent
  if (sent === null) return null
  const sentLine = (
    <p className="sub">
      {t(
        'A rendelést elküldtük, itt már nem módosítható.',
        'Your order has been sent and can no longer be changed here.',
      )}
    </p>
  )
  if (sent.enquiry) {
    return (
      <div id="done">
        <div className="done">
          <div className="sub">{t('Ajánlatkérés', 'Enquiry')}</div>
          <div className="ref">{sent.ref}</div>
          <p>
            {t.lang === 'hu' ? (
              <>
                Köszönjük. Kollégánk felhívja a <b>{sent.phone}</b> számon. Az esemény adatait és az
                összeállított menüt megkaptuk.
              </>
            ) : (
              <>
                Thank you. A colleague will call you on <b>{sent.phone}</b>. We have the event details
                and the menu you put together.
              </>
            )}
          </p>
          {sentLine}
        </div>
      </div>
    )
  }
  const eventDate = sent.eventAt === null ? null : new Date(sent.eventAt)
  const handoverDate = sent.handoverAt === null ? null : new Date(sent.handoverAt)
  /* Run 16 item 2: every delivery and pickup time the customer reads carries
     its date and its weekday, written by `eventTimeLabel` in the engine and by
     nothing else. The instant is read in Europe/Budapest first, because the
     visitor's browser may be anywhere (§10). */
  const eventLine =
    eventDate === null ? '' : eventTimeLabel(f.lang, budapestDate(eventDate), sent.slot)
  const handoverLine =
    handoverDate === null
      ? eventLine
      : eventTimeLabel(f.lang, budapestDate(handoverDate), sent.slot)
  /* `build/FIXES.md` item 79 (F8): the order is placed, priced and holding
     its date — and **nothing has been paid**. The three steps below are the
     pay-first lane's and would be wrong here in all three: there is no card
     payment next, no approval after payment, and no confirmation to wait for
     until a colleague has answered. So the panel says what did happen and
     what happens next, and nothing else on this page is changed. */
  if (sent.confirmation) {
    return (
      <div id="done">
        <div className="done" data-confirmation="true">
          <div className="sub">{t('Rendelésazonosító', 'Order reference')}</div>
          <div className="ref">{sent.ref}</div>
          <p>
            {t.lang === 'hu' ? (
              <>
                Köszönjük. Elküldtük visszaigazolásra, és értesítést küldtünk erre a címre:{' '}
                <b>{sent.email}</b>. <b>Most nem kell fizetnie.</b>
              </>
            ) : (
              <>
                Thank you. We have sent it for confirmation and emailed an acknowledgement to{' '}
                <b>{sent.email}</b>. <b>There is nothing to pay now.</b>
              </>
            )}
          </p>
          <ConfirmationNote page={page} />
          {sentLine}
        </div>
      </div>
    )
  }
  return (
    <div id="done">
      <div className="done">
        <div className="sub">{t('Rendelésazonosító', 'Order reference')}</div>
        <div className="ref">{sent.ref}</div>
        <p>
          {t.lang === 'hu' ? (
            <>
              Köszönjük. Értesítést küldtünk erre a címre: <b>{sent.email}</b>. Ez még nem elfogadás.
            </>
          ) : (
            <>
              Thank you. We have sent an acknowledgement to <b>{sent.email}</b>. That is not yet
              acceptance.
            </>
          )}
        </p>
        <ol>
          {/* `build/FIXES.md` item 83 (P4): the card step was one sentence,
              *card payment comes next; we reserve the full amount*, and it is
              deleted with nothing in its place, so on the card lane the list
              starts at the confirmation. The transfer step is unchanged. */}
          {sent.payment === 'transfer' ? (
            <li>
              {t(
                'A díjbekérőt e-mailben kapja meg. A rendelést a befizetés után dolgozzuk fel.',
                'We send the payment request by email. We process the order once it is paid.',
              )}
            </li>
          ) : null}
          <li>
            {sent.reviewed
              ? t(
                  'Fizetés után kollégánk jóváhagyja, és visszaigazoljuk.',
                  'After payment a colleague approves it and we confirm.',
                )
              : /* vB6's English. `build/FIXES.md` item 17 records this
                   sentence as deliberately left in the passive, because
                   DR-14's list of seven does not reach it; closing it here
                   would be the port taking a copy decision of its own. It
                   stays an issue for rev8. */
                t(
                  'Fizetés után automatikusan visszaigazoljuk.',
                  'Once paid, it is confirmed automatically.',
                )}
          </li>
          <li>
            {/* Run 16 item 2: the label is the one that was here; what follows
                it is the one shared formatter's line — date, weekday, window. */}
            {sent.pickup
              ? t(
                  `Átvétel a Graphisoft Parkban, ${handoverLine === '' ? '—' : handoverLine}.`,
                  `Pickup at Graphisoft Park, ${handoverLine === '' ? '—' : handoverLine}.`,
                )
              : t(
                  `Kiszállítás ${eventLine === '' ? '—' : eventLine}, nagy dobozokban.`,
                  `Delivery on ${eventLine === '' ? '—' : eventLine}, in big boxes.`,
                )}
          </li>
          <li>
            {t(
              'Az esemény utáni munkanapon néhány rövid kérdést küldünk: elég volt-e. Ebből tanulunk.',
              'On the business day after the event we will send a few short questions, including whether there was enough. That is how we learn.',
            )}
          </li>
        </ol>
        <div className="sumrow big" style={{ marginTop: 14 }}>
          <span>{t('Fizetendő', 'Payable')}</span>
          <span className="num">{f.money(sent.gross)}</span>
        </div>
        {sent.quoted ? (
          <p className="sub" style={{ margin: '4px 0 0' }}>
            {t(
              'A szállítási díjat egyedileg adjuk meg; az összeg nem tartalmazza.',
              'The delivery fee is quoted individually and not included.',
            )}
          </p>
        ) : null}
        {/* §9.6: the reservation did not start. The order stands and its
            reference is above; what the customer needs is the way back to the
            payment, not a second order. */}
        {state.paymentError && sent.publicToken !== null ? (
          <Note tone="stop" id="paymentError">
            {t.lang === 'hu' ? (
              <>
                A fizetést most nem tudtuk elindítani. A rendelés megvan, a száma fent olvasható.{' '}
                <a href={`/fizetes/${encodeURIComponent(sent.publicToken)}?lang=${t.lang}`}>
                  Próbálja újra itt
                </a>
                , vagy hívjon minket.
              </>
            ) : (
              <>
                We could not start the payment. Your order stands and its reference is above.{' '}
                <a href={`/fizetes/${encodeURIComponent(sent.publicToken)}?lang=${t.lang}`}>
                  Try again here
                </a>
                , or call us.
              </>
            )}
          </Note>
        ) : null}
        {sentLine}
      </div>
    </div>
  )
}
